/* eslint-disable max-lines-per-function */
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  IProvidedColumn,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { Signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { AgExpandableGroupHeaderComponent } from '@widgets/ag-expandable-group-header/ag-expandable-group-header.component';

import { AgCellWrapperComponent } from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import { TableConstants } from '@shared/constants/table.constants';
import { CurrencyToggle } from '@shared/types/components.type';
import { AuxExcelStyleKeys } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { PatientTrackerRow, Visit } from '../types';
import {
  isVisitMonthBeforeCurrentOpenMonth,
  isVisitMonthEqualWithCurrentOpenMonth,
} from './visit-date.utils';

const MAX_WIDTH = 150;
const MIN_WIDTH_PATIENT_COST_COLUMN = 200;

const multipleCurrencyFormatter = (
  params: ValueFormatterParams,
  selectedVisibleCurrency$: BehaviorSubject<CurrencyToggle>,
  contractedFieldName: keyof PatientTrackerRow
) => {
  if (!params?.data) {
    return '';
  }

  const isContracted = selectedVisibleCurrency$.getValue() === CurrencyToggle.CONTRACTED;

  return Utils.agMultipleCurrencyFormatter(selectedVisibleCurrency$)({
    ...params,
    value: isContracted ? params.data[contractedFieldName] : params.value,
  });
};

const highlightPlannedVisitCell = (
  params: CellClassParams,
  showVisits: Signal<boolean>,
  protocolId: string,
  flag: 'isPlannedThroughEOT' | 'shouldHighlight'
) => {
  if (!params?.data) {
    return false;
  }

  return showVisits() && params.data[`${protocolId}::${flag}`];
};

const hideValueForNonCurrentMonthCells = ({
  completed,
  currentOpenMonth,
  formatterCallback,
  isPlannedThroughEOT,
  isTotalRow,
  shouldHighlight,
  showPannedEOT,
  showPlannedVisits,
  value,
}: {
  completed: boolean;
  currentOpenMonth: string;
  formatterCallback: () => string;
  isPlannedThroughEOT: boolean;
  isTotalRow: boolean;
  shouldHighlight: boolean;
  showPannedEOT: Signal<boolean>;
  showPlannedVisits: Signal<boolean>;
  value: string;
}): string => {
  if (isTotalRow) {
    return formatterCallback();
  }

  const isCurrentMonth = isVisitMonthEqualWithCurrentOpenMonth(value, currentOpenMonth);
  const isVisitBeforeCurrentPeriod = isVisitMonthBeforeCurrentOpenMonth(value, currentOpenMonth);

  if (showPlannedVisits() && !completed && isVisitBeforeCurrentPeriod) {
    return formatterCallback();
  }

  if (showPannedEOT() && isPlannedThroughEOT) {
    return formatterCallback();
  }

  if ((shouldHighlight && !showPlannedVisits()) || (!completed && !isCurrentMonth)) {
    return Utils.zeroHyphen;
  }

  return formatterCallback();
};

export const getPatientsColumn = (
  navigateToSite: ColDef['onCellClicked'],
  openInvestigatorTransAction: ColDef['onCellClicked']
): ColGroupDef => ({
  children: [
    {
      field: 'site_id',
      hide: true,
    },
    {
      cellClass: (param: CellClassParams) => {
        return param.value !== 'Total'
          ? 'aux-link cursor-pointer ag-cell-align-right'
          : 'ag-cell-align-right';
      },
      cellStyle: (params) => {
        return params.value !== 'Total' ? { color: '#095B95' } : undefined;
      },
      field: 'external_patient_id',
      headerName: 'Patient ID',
      maxWidth: MAX_WIDTH,
      onCellClicked: (event) =>
        openInvestigatorTransAction ? openInvestigatorTransAction(event) : undefined,
      pinned: 'left',
      resizable: true,
      tooltipField: 'external_patient_id',
    },
    {
      cellClass: 'aux-link cursor-pointer',
      colId: 'site_no',
      field: 'site_no',
      headerName: 'Site No.',
      maxWidth: MAX_WIDTH,
      onCellClicked: (event) => (navigateToSite ? navigateToSite(event) : undefined),
      pinned: 'left',
      resizable: true,
      sort: 'asc',
      tooltipField: 'site_no',
    },
  ],
  headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
  headerName: 'Patients',
});

export const getPatientCostColumns = (
  getFormattedTooltip: ColDef['tooltipValueGetter'],
  selectedVisibleCurrency$: BehaviorSubject<CurrencyToggle>,
  showForecastCostThroughEOT: boolean
): ColGroupDef => ({
  children: [
    {
      cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'totalVisitCostsToDate',
      headerName: 'Total Visit Costs to Date',
      maxWidth: MAX_WIDTH * 2,
      minWidth: MIN_WIDTH_PATIENT_COST_COLUMN,
      resizable: true,
      sortable: false,
      valueFormatter: (params) =>
        multipleCurrencyFormatter(
          params,
          selectedVisibleCurrency$,
          'totalVisitCostsToDateContracted'
        ),
    },
    {
      cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'totalCostsFromOtherVersions',
      headerName: 'Total Costs from Other Versions',
      maxWidth: MAX_WIDTH * 2,
      minWidth: MIN_WIDTH_PATIENT_COST_COLUMN,
      resizable: true,
      sortable: false,
      valueFormatter: (params) =>
        multipleCurrencyFormatter(
          params,
          selectedVisibleCurrency$,
          'totalCostsFromOtherVersionsContracted'
        ),
    },
    {
      cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'totalInvoiceablesToDate',
      headerName: 'Total Invoiceables to Date',
      maxWidth: MAX_WIDTH * 2,
      minWidth: MIN_WIDTH_PATIENT_COST_COLUMN,
      resizable: true,
      sortable: false,
      valueFormatter: (params) =>
        multipleCurrencyFormatter(
          params,
          selectedVisibleCurrency$,
          'totalInvoiceablesToDateContracted'
        ),
    },
    {
      cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'totalLTDCosts',
      headerName: 'Total LTD Costs',
      maxWidth: MAX_WIDTH * 2,
      minWidth: MIN_WIDTH_PATIENT_COST_COLUMN,
      resizable: true,
      sortable: false,
      valueFormatter: (params) =>
        multipleCurrencyFormatter(params, selectedVisibleCurrency$, 'totalLTDCostsContracted'),
    },
    showForecastCostThroughEOT
      ? {
          cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
          field: 'totalForecastCostThroughEot',
          headerName: 'Total Forecast Cost through EOT',
          maxWidth: MAX_WIDTH * 2,
          minWidth: MIN_WIDTH_PATIENT_COST_COLUMN,
          resizable: true,
          sortable: false,
          tooltipValueGetter: getFormattedTooltip,
          valueFormatter: (params) =>
            multipleCurrencyFormatter(
              params,
              selectedVisibleCurrency$,
              'totalForecastCostThroughEotContracted'
            ),
        }
      : {
          hide: true,
        },
    {
      cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'forecastRemaining',
      headerName: 'Forecasted Remaining',
      maxWidth: MAX_WIDTH * 2,
      minWidth: MIN_WIDTH_PATIENT_COST_COLUMN,
      resizable: true,
      sortable: false,
      valueFormatter: (params) =>
        multipleCurrencyFormatter(params, selectedVisibleCurrency$, 'forecastRemainingContracted'),
    },
  ],
  groupId: 'patientCosts',
  headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
  headerGroupComponent: AgExpandableGroupHeaderComponent,
  headerGroupComponentParams: {
    collapsedByDefault: true,
    expandableCols: [
      'totalVisitCostsToDate',
      'totalCostsFromOtherVersions',
      'totalInvoiceablesToDate',
      'totalForecastCostThroughEot',
      'forecastRemaining',
    ],
    filterCols(column: IProvidedColumn) {
      return !['totalLTDCosts'].includes(column.getId());
    },
    localStorageKey: 'patient_tracker_page_patient_costs',
  },
  headerName: 'Patient Costs',
});
export const getPatientVisitColumns = ({
  currentOpenMonth,
  getFormattedTooltip,
  protocols,
  selectedVisibleCurrency$,
  showPannedEOT,
  showPlannedVisits,
}: {
  currentOpenMonth: string;
  getFormattedTooltip: ColDef['tooltipValueGetter'];
  protocols: Visit[];
  selectedVisibleCurrency$: BehaviorSubject<CurrencyToggle>;
  showPannedEOT: Signal<boolean>;
  showPlannedVisits: Signal<boolean>;
}): ColGroupDef => ({
  children: protocols
    .map((protocol) => [
      {
        ...TableConstants.dynamicColumnProps(protocol.name),
        cellClassRules: {
          '!bg-aux-blue-arctic': (params: CellClassParams) =>
            highlightPlannedVisitCell(params, showPannedEOT, protocol.id, 'isPlannedThroughEOT'),
          '!bg-aux-green-2': (params: CellClassParams) =>
            highlightPlannedVisitCell(params, showPlannedVisits, protocol.id, 'shouldHighlight'),
        },
        cellRenderer: AgCellWrapperComponent,
        cellRendererParams: { customLocator: 'dates' },
        field: `${protocol.id}::dates`,
        maxWidth: MAX_WIDTH,
        resizable: true,
        sortable: false,
        tooltipValueGetter: getFormattedTooltip,
        type: 'date',
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params?.data) {
            return '';
          }

          return hideValueForNonCurrentMonthCells({
            completed: params.data[`${protocol.id}::completed`],
            currentOpenMonth,
            formatterCallback: () => Utils.agDateFormatter(params),
            isPlannedThroughEOT: params.data[`${protocol.id}::isPlannedThroughEOT`],
            isTotalRow: params.data.external_patient_id === 'Total',
            shouldHighlight: params.data[`${protocol.id}::shouldHighlight`],
            showPannedEOT,
            showPlannedVisits,
            value: params.data[`${protocol.id}::dates`],
          });
        },
      },
      {
        ...TableConstants.dynamicColumnProps(protocol.name),
        cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
        cellClassRules: {
          '!bg-aux-blue-arctic': (params: CellClassParams) =>
            highlightPlannedVisitCell(params, showPannedEOT, protocol.id, 'isPlannedThroughEOT'),
          '!bg-aux-green-2': (params: CellClassParams) =>
            highlightPlannedVisitCell(params, showPlannedVisits, protocol.id, 'shouldHighlight'),
        },
        cellRenderer: AgCellWrapperComponent,
        cellRendererParams: { customLocator: 'costs' },
        field: `${protocol.id}::costs`,
        hide: true,
        maxWidth: 500,
        resizable: true,
        sortable: false,
        tooltipValueGetter: getFormattedTooltip,
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params?.data) {
            return '';
          }

          return hideValueForNonCurrentMonthCells({
            completed: params.data[`${protocol.id}::completed`],
            currentOpenMonth,
            formatterCallback: () =>
              multipleCurrencyFormatter(
                params,
                selectedVisibleCurrency$,
                `${protocol.id}::costs::contracted`
              ),
            isPlannedThroughEOT: params.data[`${protocol.id}::isPlannedThroughEOT`],
            isTotalRow: params.data.external_patient_id === 'Total',
            shouldHighlight: params.data[`${protocol.id}::shouldHighlight`],
            showPannedEOT,
            showPlannedVisits,
            value: params.data[`${protocol.id}::dates`],
          });
        },
      },
    ])
    .flat(),
  headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
  headerName: 'Visits',
});
